import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, Box, Button } from '@material-ui/core';
import { Add as AddIcon, RemoveCircle } from '@material-ui/icons';
import Subtitle2 from '../../../common/typographys/Subtitle2';
import SelectOutlined from '../../../common/forms/SelectOutlined';
import TimeBox from '../../../configs/pieces/TimeBox';
import Budget from './Budget';
import MultipleBudgets from './MultipleBudgets';

const useStyles = makeStyles(theme => ({
  label: {
    marginBottom: '6px',
  },
  entitySelect: {
    flex: 2,
  },
  addButton: {
    marginBottom: '20px',
  },
  entityMainBox: {
    position: 'relative'
  },
  removeIconBox: {
    position: 'absolute',
    top: '8px',
    left: '10px',
    right: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
  },
  removeIcon: {
    color: theme.palette.neutrals[4],
    transform: 'scale(0.666666666)',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.colorsPalette.RustRed,
    },
  },
}));

const MultipleEntities = ({ state, updateField }) => {
  const classes = useStyles();

  const { entities = [], errors = {}, multipleBudgets = false } = state;
  const { entities: entitiesOptions = [] } = useSelector(stateRedux => stateRedux.entities);

  // const budgetsEnv = process.env.REACT_APP_PROPOSAL_ENTITIES_BUDGETS || 'single';
  // const budgetsMode = ['single', 'multiple'].includes(budgetsEnv) ? budgetsEnv : 'single';

  // const handleAddEntity = useCallback(() => {
  //   const newEntities = [...entities, { id: Date.now(), entity: null, amount: '' }];
  //   updateField('entities', newEntities);
  // }, [entities, updateField]);

  const handleAddEntity = useCallback(() => {
    const newEntity = multipleBudgets
      ? { id: Date.now(), entity: null, hasBudget: false, budgets: [] }
      : { id: Date.now(), entity: null, hasBudget: false, budgetNumber: '', amount: '' };

    const newEntities = [...entities, newEntity];
    updateField('entities', newEntities);
  }, [entities, updateField, multipleBudgets]);

  const handleRemoveEntity = useCallback(
    index => {
      const newEntities = entities.filter((_, idx) => idx !== index);
      updateField('entities', newEntities);
    },
    [entities, updateField]
  );

  const handleEntityChange = useCallback(
    (index, value) => {
      const newEntities = [...entities];
      newEntities[index] = { ...newEntities[index], entity: value };
      updateField('entities', newEntities);
    },
    [entities, updateField]
  );

  // const handleBudgetFieldChange = useCallback(
  //   (index, fieldName, value) => {
  //     const newEntities = [...entities];
  //     newEntities[index] = { ...newEntities[index], [fieldName]: value };
  //     updateField('entities', newEntities);
  //   },
  //   [entities, updateField]
  // );

  const handleBudgetFieldChange = useCallback(
    (index, fieldName, value, initialBudgets) => {
      const newEntities = [...entities];

      if (initialBudgets) {
        newEntities[index] = {
          ...newEntities[index],
          [fieldName]: value,
          budgets: initialBudgets
        };
      } else {
        newEntities[index] = { ...newEntities[index], [fieldName]: value };
      }
      updateField('entities', newEntities);
    },
    [entities, updateField]
  );

  const getAvailableEntities = useCallback((currentIndex) => {
    const selectedEntityIds = entities
      .map((item, idx) => idx !== currentIndex && item.entity ? item.entity.id : null)
      .filter(id => id !== null);

    return entitiesOptions.filter(entity => !selectedEntityIds.includes(entity.id));

    // .map((item) => {
    //   return {
    //     id: item.id,
    //     name: item.name
    //   };
    // });
  }, [entities, entitiesOptions]);

  return (
    <>
      <Subtitle2 className={classes.label}>Entidades</Subtitle2>

      {entities.map((item, index) => {
        // const budgetState = {
        //   hasBudget: item.hasBudget || false,
        //   budgetNumber: item.budgetNumber || '',
        //   amount: item.amount || '',
        //   errors: {
        //     budgetNumber: errors[`multipleEntityBudgetNumber${index}`] || '',
        //     amount: errors[`multipleEntityAmount${index}`] || '',
        //   }
        // };

        // const updateBudgetField = (fieldName, value) => {
        //   handleBudgetFieldChange(index, fieldName, value);
        // };

        // const availableEntities = getAvailableEntities(index);


        // const updateEntityBudgetField = (fieldName, value) => {
        //   handleBudgetFieldChange(index, fieldName, value);
        // };

        const updateEntityBudgetField = (fieldName, value, initialBudgets) => {
          handleBudgetFieldChange(index, fieldName, value, initialBudgets);
        };

        const availableEntities = getAvailableEntities(index);

        let budgetComponent;

        if (multipleBudgets) {
          const budgetErrors = {};

          (item.budgets || []).forEach((_, budgetIndex) => {
            const budgetNumberErrorKey = `entity${index}BudgetNumber${budgetIndex}`;
            const amountErrorKey = `entity${index}Amount${budgetIndex}`;
            const interventionErrorKey = `entity${index}Intervention${budgetIndex}`;

            if (errors[budgetNumberErrorKey]) {
              budgetErrors[`budgetNumber${budgetIndex}`] = errors[budgetNumberErrorKey];
            }
            if (errors[amountErrorKey]) {
              budgetErrors[`amount${budgetIndex}`] = errors[amountErrorKey];
            }
            if (errors[interventionErrorKey]) {
              budgetErrors[`intervention${budgetIndex}`] = errors[interventionErrorKey];
            }
          });

          const multipleBudgetState = {
            hasBudget: item.hasBudget || false,
            budgets: item.budgets || [],
            errors: budgetErrors,
          };

          budgetComponent = (
            <MultipleBudgets
              state={multipleBudgetState}
              updateField={updateEntityBudgetField}
              entityIndex={index}
            />
          );
        } else {
          // Single budget per entity mode (original behavior)
          const singleBudgetState = {
            hasBudget: item.hasBudget || false,
            budgetNumber: item.budgetNumber || '',
            amount: item.amount || '',
            errors: {
              budgetNumber: errors[`multipleEntityBudgetNumber${index}`] || '',
              amount: errors[`multipleEntityAmount${index}`] || '',
            }
          };

          budgetComponent = (
            <Budget
              state={singleBudgetState}
              updateField={updateEntityBudgetField}
            />
          );
        }

        return (
          // <Box key={item.id} className={clsx(classes.entityMainBox)}>
          <Box key={`entity-${index}`} className={clsx(classes.entityMainBox)}>
            <TimeBox dense>

              <Box className={clsx(classes.removeIconBox)}>
                <RemoveCircle
                  className={classes.removeIcon}
                  onClick={() => {
                    // e.preventDefault();
                    handleRemoveEntity(index);
                  }}
                />
              </Box>

              {/* <Box className={classes.entityHeader}> */}
              <Box className={classes.entitySelect}>
                <SelectOutlined
                  isClearable
                  name={`multipleEntity-${index}`}
                  label="Entidade"
                  value={item.entity}
                  options={availableEntities}
                  onChange={val => handleEntityChange(index, val)}
                  getOptionLabel={option => option.name || ''}
                  getOptionValue={option => option.id || ''}
                  error={errors[`multipleEntity${index}`]}
                // helperText={errors[`multipleEntity${index}`] || ''}
                />
              </Box>
              {/* </Box> */}

              {/* <Budget
                state={budgetState}
                updateField={updateBudgetField}
              /> */}
              {budgetComponent}

            </TimeBox>

            {index + 1 !== entities.length && <Box height="4px" />}
          </Box>
        );
      })}

      <Box height="8px" />

      <Button
        startIcon={<AddIcon />}
        variant="text"
        color="primary"
        size="small"
        onClick={handleAddEntity}
        className={classes.addButton}
      >
        Adicionar Entidade
      </Button>
    </>
  );
};

MultipleEntities.propTypes = {
  state: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
};

export default React.memo(MultipleEntities);
